import React, { useRef, useEffect, useState } from 'react';
import styles from '../style';

const ContactForm = () => {
  const formRef = useRef();
  const [formStatus, setFormStatus] = useState('idle'); // idle, submitting, success, error
  const [focusedField, setFocusedField] = useState('');

  useEffect(() => {
    const handleUnload = () => {
      formRef.current?.reset();
    };

    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('submitting');
    
    const formData = new FormData(formRef.current);
    
    try {
      const response = await fetch('https://formspree.io/f/xayggrwy', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        setFormStatus('success');
        formRef.current.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  const contactMethods = [
    {
      icon: (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      title: "Email",
      value: "contact@jc-techs.com",
      link: "mailto:contact@jc-techs.com"
    },
    {
      icon: (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      title: "Location",
      value: "Tacoma, Washington",
      link: null
    },
    {
      icon: (
        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: "Response Time",
      value: "Within 24 hours",
      link: null
    }
  ];

  return (
    <section className={`${styles.flexCenter} flex-col ${styles.paddingY} bg-gradient-to-b from-white to-gray-50`}>
      <div className="w-full max-w-7xl px-4">
        
        {/* Hero Section */}
        <div className="relative mb-16 overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-12 text-white shadow-2xl md:p-16">
          {/* Animated Background */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute h-96 w-96 -left-48 -top-48 rounded-full bg-blue-500 blur-3xl animate-pulse"></div>
            <div className="absolute h-96 w-96 -right-48 -bottom-48 rounded-full bg-emerald-500 blur-3xl animate-pulse delay-1000"></div>
          </div>
          
          <div className="relative z-10 text-center">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur border border-white/20 px-4 py-2 text-sm">
              <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse"></span>
              <span>We respond within 24 hours</span>
            </div>
            
            <h1 className="mb-6 text-4xl font-bold md:text-5xl lg:text-6xl">
              Let's Build Your
              <span className="block bg-gradient-to-r from-blue-400 to-emerald-400 bg-clip-text text-transparent">
                Street Intelligence Platform
              </span>
            </h1>
            
            <p className="mx-auto mb-8 max-w-2xl text-lg text-gray-300">
              Ready to transform your city's infrastructure management? 
              Schedule a demo, request a pilot, or just say hello.
            </p>
          </div>
        </div>

        {/* Contact Methods Cards */}
        <div className="mb-16 grid gap-6 md:grid-cols-3">
          {contactMethods.map((method, index) => (
            <div 
              key={index}
              className="group relative overflow-hidden rounded-2xl bg-white p-6 shadow-lg transition-all hover:shadow-xl hover:scale-105"
            >
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-emerald-500 text-white">
                  {method.icon}
                </div>
                <div className="flex-1">
                  <h3 className="mb-1 font-semibold text-gray-900">{method.title}</h3>
                  {method.link ? (
                    <a href={method.link} className="text-gray-600 hover:text-blue-600 transition">
                      {method.value}
                    </a>
                  ) : (
                    <p className="text-gray-600">{method.value}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Main Contact Section */}
        <div className="grid gap-12 lg:grid-cols-2">
          
          {/* Left Side - Info */}
          <div className="relative">
            <div className="sticky top-8">
              <div className="mb-8">
                <h2 className="mb-4 text-3xl font-bold text-gray-900">
                  Start Your Transformation Journey
                </h2>
                <p className="text-lg text-gray-600">
                  Whether you're ready for a full deployment or just exploring options, 
                  we're here to help you navigate the future of street infrastructure management.
                </p>
              </div>

              {/* What to Expect */}
              <div className="rounded-2xl bg-gradient-to-br from-gray-50 to-white p-6 border border-gray-100">
                <h3 className="mb-4 text-xl font-bold text-gray-900">What to Expect</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="mt-1 h-5 w-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <div className="h-2 w-2 rounded-full bg-emerald-500"></div>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Quick Response</p>
                      <p className="text-sm text-gray-600">We'll get back to you within 24 hours</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="mt-1 h-5 w-5 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <div className="h-2 w-2 rounded-full bg-blue-500"></div>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Personalized Demo</p>
                      <p className="text-sm text-gray-600">See our platform in action with your use cases</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="mt-1 h-5 w-5 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <div className="h-2 w-2 rounded-full bg-purple-500"></div>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Custom Pilot Program</p>
                      <p className="text-sm text-gray-600">Start small with a proof of concept in your city</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Trust Badges */}
              <div className="mt-6 flex flex-wrap gap-4">
                <span className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  SOC2 Compliant
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Trusted by Cities
                </span>
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="relative">
            <div className="rounded-2xl bg-white p-8 shadow-xl">
              <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
                
                {/* Name Fields */}
                <div className="grid gap-6 sm:grid-cols-2">
                  <div className="relative">
                    <label htmlFor="firstname" className="mb-2 block text-sm font-medium text-gray-700">
                      First Name
                    </label>
                    <input
                      type="text"
                      id="firstname"
                      name="firstname"
                      required
                      onFocus={() => setFocusedField('firstname')}
                      onBlur={() => setFocusedField('')}
                      className={`block w-full rounded-xl border-2 px-4 py-3 transition-all ${
                        focusedField === 'firstname' 
                          ? 'border-blue-500 shadow-lg shadow-blue-500/20' 
                          : 'border-gray-200 hover:border-gray-300'
                      } focus:outline-none`}
                      placeholder="John"
                    />
                  </div>
                  
                  <div className="relative">
                    <label htmlFor="lastname" className="mb-2 block text-sm font-medium text-gray-700">
                      Last Name
                    </label>
                    <input
                      type="text"
                      id="lastname"
                      name="lastname"
                      required
                      onFocus={() => setFocusedField('lastname')}
                      onBlur={() => setFocusedField('')}
                      className={`block w-full rounded-xl border-2 px-4 py-3 transition-all ${
                        focusedField === 'lastname' 
                          ? 'border-blue-500 shadow-lg shadow-blue-500/20' 
                          : 'border-gray-200 hover:border-gray-300'
                      } focus:outline-none`}
                      placeholder="Doe"
                    />
                  </div>
                </div>

                {/* Email Field */}
                <div className="relative">
                  <label htmlFor="email" className="mb-2 block text-sm font-medium text-gray-700">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => setFocusedField('')}
                    className={`block w-full rounded-xl border-2 px-4 py-3 transition-all ${
                      focusedField === 'email' 
                        ? 'border-blue-500 shadow-lg shadow-blue-500/20' 
                        : 'border-gray-200 hover:border-gray-300'
                    } focus:outline-none`}
                    placeholder="john.doe@city.gov"
                  />
                </div>

                {/* Organization Field */}
                <div className="relative">
                  <label htmlFor="organization" className="mb-2 block text-sm font-medium text-gray-700">
                    Organization (Optional)
                  </label>
                  <input
                    type="text"
                    id="organization"
                    name="organization"
                    onFocus={() => setFocusedField('organization')}
                    onBlur={() => setFocusedField('')}
                    className={`block w-full rounded-xl border-2 px-4 py-3 transition-all ${
                      focusedField === 'organization' 
                        ? 'border-blue-500 shadow-lg shadow-blue-500/20' 
                        : 'border-gray-200 hover:border-gray-300'
                    } focus:outline-none`}
                    placeholder="City of..."
                  />
                </div>

                {/* Message Field */}
                <div className="relative">
                  <label htmlFor="message" className="mb-2 block text-sm font-medium text-gray-700">
                    How can we help you?
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    required
                    onFocus={() => setFocusedField('message')}
                    onBlur={() => setFocusedField('')}
                    className={`block w-full rounded-xl border-2 px-4 py-3 transition-all ${
                      focusedField === 'message' 
                        ? 'border-blue-500 shadow-lg shadow-blue-500/20' 
                        : 'border-gray-200 hover:border-gray-300'
                    } focus:outline-none resize-none`}
                    placeholder="Tell us about your project or request a demo..."
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className={`group relative w-full overflow-hidden rounded-xl px-8 py-4 font-semibold text-white shadow-lg transition-all ${
                    formStatus === 'submitting' 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-gradient-to-r from-blue-600 to-emerald-600 hover:shadow-xl hover:scale-105'
                  }`}
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    {formStatus === 'submitting' ? (
                      <>
                        <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                        <span>Send Message</span>
                      </>
                    )}
                  </span>
                  {formStatus !== 'submitting' && (
                    <div className="absolute inset-0 -translate-x-full bg-white/20 transition-transform group-hover:translate-x-0"></div>
                  )}
                </button>

                {/* Form Status Messages */}
                {formStatus === 'success' && (
                  <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-center">
                    <p className="text-emerald-700 font-medium">✓ Message sent successfully! We'll be in touch soon.</p>
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-center">
                    <p className="text-red-700 font-medium">Something went wrong. Please try again or email us directly.</p>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default ContactForm;